import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Capacitor's Filesystem bridge accepts base64, which temporarily amplifies
// the in-memory payload. Bound the original response/blob before conversion so
// a large download cannot exhaust the WKWebView process.
export const MAX_NATIVE_DOWNLOAD_BYTES = 20 * 1024 * 1024;
const NATIVE_DOWNLOAD_SIZE_ERROR = 'This file is too large to open on this device (20 MB maximum)';
const NATIVE_DOWNLOAD_STREAM_ERROR = 'This file could not be downloaded safely on this device';
const MAX_NATIVE_FILENAME_BYTES = 180;
let nativeDownloadSequence = 0;

export interface DownloadBlobOptions {
  /** Re-checks ownership/authorization immediately before irreversible delivery steps. */
  assertCanDeliver?: () => void;
}

function assertNativeDownloadSize(blob: Blob): void {
  if (blob.size > MAX_NATIVE_DOWNLOAD_BYTES) {
    throw new Error(NATIVE_DOWNLOAD_SIZE_ERROR);
  }
}

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function truncateUtf8(value: string, maxBytes: number): string {
  let result = '';
  let byteLength = 0;

  for (const character of value) {
    const characterBytes = utf8Length(character);
    if (byteLength + characterBytes > maxBytes) break;
    result += character;
    byteLength += characterBytes;
  }

  return result;
}

function truncateFilename(value: string): string {
  if (utf8Length(value) <= MAX_NATIVE_FILENAME_BYTES) return value;

  // Preserve a short extension so receiving apps can still infer the file
  // type after a long display name is truncated.
  const extensionIndex = value.lastIndexOf('.');
  const extension = extensionIndex > 0 ? value.slice(extensionIndex) : '';
  const safeExtension = utf8Length(extension) <= 24 ? extension : '';
  const stem = safeExtension ? value.slice(0, extensionIndex) : value;
  return `${truncateUtf8(stem, MAX_NATIVE_FILENAME_BYTES - utf8Length(safeExtension))}${safeExtension}`;
}

function safeNativeFilename(filename: string): string {
  const sanitized = Array.from(filename.normalize('NFKC'))
    .map((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      const isControl =
        (codePoint >= 0 && codePoint <= 31) || (codePoint >= 127 && codePoint <= 159);
      const isFormatControl = /\p{Cf}/u.test(character);
      return isControl || isFormatControl || '/\\:'.includes(character) ? '_' : character;
    })
    .join('')
    .replace(/^\.+/, '')
    .trim();
  return truncateFilename(sanitized || 'download');
}

function uniqueNativeCachePath(filename: string): { directory: string; path: string } {
  nativeDownloadSequence = (nativeDownloadSequence + 1) % Number.MAX_SAFE_INTEGER;
  const uniqueId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${nativeDownloadSequence}`;
  const directory = `downloads/${uniqueId}`;
  return { directory, path: `${directory}/${filename}` };
}

/**
 * Read a download response safely. Web callers retain the browser's normal
 * Blob behavior; native callers validate Content-Length and then enforce the
 * same limit while streaming when the length is absent or untrusted.
 */
export async function readDownloadResponseBlob(response: Response): Promise<Blob> {
  if (!Capacitor.isNativePlatform()) return response.blob();

  const rawLength = response.headers.get('content-length');
  const declaredLength = rawLength === null ? Number.NaN : Number(rawLength);
  if (Number.isFinite(declaredLength) && declaredLength > MAX_NATIVE_DOWNLOAD_BYTES) {
    await response.body?.cancel().catch(() => undefined);
    throw new Error(NATIVE_DOWNLOAD_SIZE_ERROR);
  }

  // Buffering an unstreamable response before checking Blob.size defeats the
  // native memory bound. Modern Capacitor WebViews support response streams,
  // so fail closed if a response cannot be inspected incrementally.
  const reader = response.body?.getReader();
  if (!reader) throw new Error(NATIVE_DOWNLOAD_STREAM_ERROR);

  const chunks: ArrayBuffer[] = [];
  let totalBytes = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      totalBytes += value.byteLength;
      if (totalBytes > MAX_NATIVE_DOWNLOAD_BYTES) {
        await reader.cancel().catch(() => undefined);
        throw new Error(NATIVE_DOWNLOAD_SIZE_ERROR);
      }
      chunks.push(value.slice().buffer as ArrayBuffer);
    }
  } finally {
    reader.releaseLock();
  }

  return new Blob(chunks, { type: response.headers.get('content-type') || undefined });
}

/**
 * Convert a Blob to a base64 string (without the data-URL prefix).
 * Required by the Capacitor Filesystem API which expects base64-encoded data.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const separator = typeof result === 'string' ? result.indexOf(',') : -1;
      if (typeof result !== 'string' || separator < 0) {
        reject(new Error('Failed to read file data for download'));
        return;
      }
      // Strip "data:<mime>;base64," prefix.
      resolve(result.slice(separator + 1));
    };
    // Reject with a proper Error so callers receive a meaningful message
    reader.onerror = () => reject(new Error('Failed to read file data for download'));
    reader.onabort = () => reject(new Error('File download was cancelled'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Download a blob as a named file.
 *
 * - **Native (iOS/Android via Capacitor):** Saves the file to the app's cache
 *   directory, then opens the system share sheet so the user can open it in
 *   another app (Files, Mail, etc.).  The `<a>.click()` approach used on the
 *   web does not work inside WKWebView and is silently ignored.
 *
 * - **Web:** Uses the standard anchor-click download approach.
 */
export async function downloadBlob(
  blob: Blob,
  filename: string,
  options: DownloadBlobOptions = {},
): Promise<void> {
  options.assertCanDeliver?.();

  if (Capacitor.isNativePlatform()) {
    assertNativeDownloadSize(blob);
    const base64Data = await blobToBase64(blob);
    options.assertCanDeliver?.();
    const safeFilename = safeNativeFilename(filename);
    const cacheFile = uniqueNativeCachePath(safeFilename);

    try {
      const { uri } = await Filesystem.writeFile({
        path: cacheFile.path,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true,
      });

      options.assertCanDeliver?.();
      await Share.share({
        title: safeFilename,
        url: uri,
        dialogTitle: `Open ${safeFilename}`,
      });
    } finally {
      // Clean up the cached file whether sharing succeeded or the user cancelled
      await Filesystem.deleteFile({ path: cacheFile.path, directory: Directory.Cache }).catch(
        () => {
          // Deletion failure is non-fatal — cache will be evicted by the OS eventually
        },
      );
      await Filesystem.rmdir({
        path: cacheFile.directory,
        directory: Directory.Cache,
        recursive: true,
      }).catch(() => {
        // Directory cleanup is best-effort for the same reason.
      });
    }
  } else {
    // Web: anchor-click download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    try {
      document.body.appendChild(a);
      options.assertCanDeliver?.();
      a.click();
    } finally {
      a.remove();
      URL.revokeObjectURL(url);
    }
  }
}
